import { useConfigContext } from '@fe-core/context';
import {
  FaIcon,
  ListItem,
  Resource,
  useMarkResourceAsRead,
} from '@fe-learner-common';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons/faArrowUpRightFromSquare';
import { faDownload } from '@fortawesome/pro-regular-svg-icons/faDownload';
import { forwardRef } from 'react';
import { buildResourceAriaLabel } from './buildResourceAriaLabel';
import { ResourceActions } from './ResourceActions';
import { ListItemProps } from './ResourceListItem';

export const Link = forwardRef<
  HTMLAnchorElement,
  Omit<ListItemProps, 'onResourceClick' | 'container'>
>(({ resource, participationId, texts, onDescriptionClick, ...rest }, ref) => {
  const { config } = useConfigContext();
  const { mutate: markAsRead } = useMarkResourceAsRead({
    resourceId: resource.rid,
    participationId,
  });
  const url =
    resource.link ||
    `${config.apiUrls.aleweb}/participation/${participationId}/resource/${resource.rid}/useDirect`;

  return (
    <ListItem>
      <a
        ref={ref}
        className="lf:absolute lf:inset-0 lf:z-0 lf:rounded-lg"
        onClick={() => {
          markAsRead();
        }}
        href={url}
        target={resource.useMode === 'newwindow' ? '_blank' : '_self'}
        rel="noreferrer"
        aria-label={buildResourceAriaLabel({
          type: texts.groupTypes[resource.groupType],
          title: resource.material.webTitle,
          tags: resource.tags,
        })}
        {...rest}
      />
      <div className="lf:flex lf:flex-1 lf:items-start lf:justify-between lf:gap-x-4 lf:p-3">
        <Resource
          resourceId={resource.rid}
          title={resource.material.webTitle}
          groupType={resource.groupType}
          thumbnail={resource.material.thumbnail}
          tags={resource.tags}
          isNew={resource.newResource}
          texts={texts}
        />
        <ResourceActions
          resourceId={resource.rid}
          hasDescription={!!resource.material.webDescription}
          onDescriptionClick={onDescriptionClick}
          descriptionAriaLabel={texts.descriptionAriaLabel}
        >
          <a
            href={url}
            target={resource.useMode === 'newwindow' ? '_blank' : '_self'}
            rel="noreferrer"
            className="lf:l-btn lf:l-btn-sm lf:l-btn-tertiary lf:l-icon-btn lf:hover:bg-neutral-50"
            onClick={() => {
              markAsRead();
            }}
            aria-label={
              resource.material.productType === 'genericfile'
                ? texts.downloadResourceAriaLabel
                : texts.openExternalResourceAriaLabel
            }
          >
            <FaIcon
              width={16}
              height={16}
              icon={
                resource.material.productType === 'genericfile'
                  ? faDownload
                  : faArrowUpRightFromSquare
              }
            />
          </a>
        </ResourceActions>
      </div>
    </ListItem>
  );
});
Link.displayName = 'LinkListItem';
